import React from "react";
import { Link } from "react-router-dom";
import {
  FaCarSide,
  FaSuitcase,
  FaUsers,
  FaMountain,
  FaSnowflake,
  FaArrowRight,
  FaGasPump,
  FaShieldAlt,
  FaMapMarkedAlt,
} from "react-icons/fa";
// Ensure you have an appropriate SUV image or use a solid background fallback
import heroImage from "../../assets/react-cars-hero-bg.jpg";

const suvClasses = [
  {
    name: "Compact & Crossover SUVs",
    models: "Toyota RAV4, Ford Escape, Honda CR-V",
    desc: "Perfect for city exploring and small families. Enjoy the elevated driving position and extra cargo space without sacrificing fuel efficiency.",
    seats: "5 Passengers",
    bags: "3-4 Bags",
  },
  {
    name: "Midsize SUVs",
    models: "Jeep Grand Cherokee, Ford Edge, Hyundai Santa Fe",
    desc: "The ideal balance of power and space. Great for longer road trips offering more legroom, advanced tech, and a smoother highway ride.",
    seats: "5 Passengers",
    bags: "4-5 Bags",
  },
  {
    name: "Full-Size & 3-Row SUVs",
    models: "Chevrolet Tahoe, Ford Expedition, Nissan Armada",
    desc: "Space for the whole crew and their gear. Offering 3 rows of seating, heavy-duty towing capacity, and ultimate comfort for large groups.",
    seats: "7-8 Passengers",
    bags: "5+ Bags",
  },
];

const faqs = [
  {
    q: "Am I guaranteed a 4-Wheel Drive (4WD) or All-Wheel Drive (AWD) vehicle?",
    a: "Not automatically. While many SUVs feature AWD or 4WD, standard reservations do not guarantee this feature unless it is specifically noted in the vehicle description (e.g., 'Jeep Wrangler 4WD' or 'Midsize SUV AWD').",
  },
  {
    q: "Do your SUVs have 3rd-row seating?",
    a: "If you need a 3rd row, you must book a 'Standard SUV' (often seats 7), a 'Full-Size SUV' (seats 7-8), or a 'Premium SUV'. Compact and Midsize SUVs typically only seat 5.",
  },
  {
    q: "Are SUVs more expensive to rent than standard cars?",
    a: "SUVs generally carry a slightly higher daily rate than economy or midsize sedans due to their size and utility. However, for groups of 5 or more, renting a single Full-Size SUV is often cheaper than renting two smaller cars.",
  },
];

export default function SUVCarRental() {
  return (
    <div className="min-h-screen bg-slate-50 font-sans text-slate-900">
      {/* HERO SECTION */}
      <section className="relative flex min-h-[85vh] items-center justify-center overflow-hidden bg-slate-900 px-4 py-20 sm:px-6">
        <div className="absolute inset-0 z-0">
          <img
            src={heroImage}
            alt="SUV Car Rental"
            className="h-full w-full object-cover object-center opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-slate-900/90 via-slate-800/70 to-slate-900" />
        </div>

        <div className="relative z-10 mx-auto max-w-4xl text-center">
          <div className="mb-6 flex justify-center">
            <span className="inline-flex items-center gap-2 rounded-full border border-blue-400/30 bg-blue-500/10 px-4 py-1.5 text-xs font-black uppercase tracking-[0.2em] text-blue-400 backdrop-blur-md">
              <FaMountain className="text-blue-400" /> Adventure Ready
            </span>
          </div>
          <h1 className="mb-6 text-5xl font-black leading-tight tracking-tight text-white sm:text-6xl lg:text-7xl">
            Space for the <br className="hidden sm:block" />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-300">
              Whole Crew.
            </span>
          </h1>
          <p className="mx-auto mb-10 max-w-2xl text-lg font-medium leading-relaxed text-slate-300 sm:text-xl">
            From compact crossovers for city exploring to 8-passenger full-size
            SUVs for epic family road trips, find the perfect vehicle with room
            for everyone and their gear.
          </p>
          <div className="flex flex-col items-center justify-center gap-4 sm:flex-row">
            <Link
              to="/cars"
              className="group flex w-full items-center justify-center gap-3 rounded-full bg-[#2871d3] px-8 py-4 text-base font-black text-white shadow-[0_0_30px_rgba(40,113,211,0.3)] transition-all duration-300 hover:-translate-y-1 hover:bg-[#1d5ab3] hover:shadow-[0_0_50px_rgba(40,113,211,0.5)] sm:w-auto"
            >
              Browse SUV Inventory
              <FaArrowRight className="transition-transform group-hover:translate-x-1" />
            </Link>
          </div>
        </div>
      </section>

      {/* TRUST BANNER */}
      <section className="border-b border-slate-200 bg-white py-8">
        <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-center gap-8 px-4 sm:gap-16 sm:px-6">
          {[
            { icon: <FaUsers />, text: "Up to 8 Passengers" },
            { icon: <FaSuitcase />, text: "Max Cargo Space" },
            { icon: <FaMapMarkedAlt />, text: "Unlimited Mileage Options" },
          ].map((item, idx) => (
            <div key={idx} className="flex items-center gap-3 text-slate-600">
              <div className="text-xl text-[#2871d3]">{item.icon}</div>
              <span className="text-sm font-bold uppercase tracking-wide">
                {item.text}
              </span>
            </div>
          ))}
        </div>
      </section>

      {/* VALUE PROPOSITION GRID */}
      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:py-24">
        <div className="mb-16 text-center">
          <h2 className="text-3xl font-black tracking-tight text-slate-900 sm:text-4xl">
            Why choose an SUV?
          </h2>
          <p className="mx-auto mt-4 max-w-2xl text-base font-medium text-slate-500">
            Versatility, safety, and comfort combine to make SUVs the most
            popular rental choice for vacations.
          </p>
        </div>

        <div className="grid gap-8 md:grid-cols-3">
          {[
            {
              icon: <FaSuitcase />,
              title: "Massive Cargo Capacity",
              desc: "Don't leave anything behind. Enjoy ample trunk space for large luggage, strollers, sports gear, and camping equipment.",
            },
            {
              icon: <FaUsers />,
              title: "Family Friendly Comfort",
              desc: "Spread out and relax. Enjoy spacious seating for 5 to 8 passengers, with plenty of room for child safety seats and long legs.",
            },
            {
              icon: <FaSnowflake />,
              title: "All-Weather Capability",
              desc: "Drive with confidence. Benefit from higher ground clearance and available AWD/4WD systems for mountain passes or winter trips.",
            },
          ].map((feature, idx) => (
            <div
              key={idx}
              className="group rounded-[2rem] border border-slate-200 bg-white p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)] transition-all duration-300 hover:-translate-y-1 hover:border-blue-200 hover:shadow-[0_20px_40px_rgba(40,113,211,0.08)]"
            >
              <div className="mb-6 flex h-16 w-16 items-center justify-center rounded-2xl bg-blue-50 text-3xl text-[#2871d3] transition-transform duration-300 group-hover:scale-110 group-hover:bg-[#2871d3] group-hover:text-white">
                {feature.icon}
              </div>
              <h3 className="mb-3 text-xl font-black text-slate-900">
                {feature.title}
              </h3>
              <p className="text-sm font-medium leading-relaxed text-slate-500">
                {feature.desc}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* SUV CLASSES (THE FLEET) */}
      <section className="bg-slate-900 py-20 text-white sm:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <div className="mb-16 text-center">
            <h2 className="text-3xl font-black tracking-tight text-white sm:text-4xl">
              Find Your Perfect Fit
            </h2>
            <p className="mx-auto mt-4 max-w-2xl text-base font-medium text-slate-400">
              From nimble city explorers to highway cruisers, we have an SUV for
              every itinerary.
            </p>
          </div>

          <div className="grid gap-6 md:grid-cols-3">
            {suvClasses.map((suvClass, idx) => (
              <div
                key={idx}
                className="flex flex-col justify-between rounded-[2rem] border border-white/10 bg-white/5 p-8 transition-colors hover:border-blue-500/50 hover:bg-white/10"
              >
                <div>
                  <div className="mb-4 text-3xl text-blue-400">
                    <FaCarSide />
                  </div>
                  <h3 className="mb-2 text-2xl font-bold">{suvClass.name}</h3>
                  <p className="mb-6 text-sm font-medium leading-relaxed text-slate-400">
                    {suvClass.desc}
                  </p>
                </div>

                <div className="space-y-3">
                  <div className="flex justify-between rounded-xl border border-white/10 bg-black/20 p-4">
                    <div className="text-center">
                      <p className="text-[0.65rem] font-bold uppercase tracking-wider text-slate-400">
                        Seats
                      </p>
                      <p className="mt-1 text-sm font-bold text-white">
                        {suvClass.seats}
                      </p>
                    </div>
                    <div className="w-px bg-white/10"></div>
                    <div className="text-center">
                      <p className="text-[0.65rem] font-bold uppercase tracking-wider text-slate-400">
                        Cargo
                      </p>
                      <p className="mt-1 text-sm font-bold text-white">
                        {suvClass.bags}
                      </p>
                    </div>
                  </div>
                  <div className="rounded-xl border border-blue-900/50 bg-blue-900/20 p-4">
                    <p className="text-[0.65rem] font-bold uppercase tracking-wider text-blue-400">
                      Typical Models
                    </p>
                    <p className="mt-1 text-sm font-medium text-slate-200">
                      {suvClass.models}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ SECTION */}
      <section className="bg-white py-20 sm:py-24">
        <div className="mx-auto max-w-4xl px-4 sm:px-6">
          <h2 className="mb-10 text-center text-3xl font-black tracking-tight text-slate-900 sm:text-4xl">
            SUV Rental FAQs
          </h2>
          <div className="space-y-4">
            {faqs.map((faq, idx) => (
              <details
                key={idx}
                className="group rounded-2xl border border-slate-200 bg-[#f8f9fc] p-6 [&_summary::-webkit-details-marker]:hidden"
              >
                <summary className="flex cursor-pointer items-center justify-between font-bold text-slate-900">
                  {faq.q}
                  <span className="ml-4 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-white text-blue-600 shadow-sm transition-transform group-open:rotate-180">
                    <svg
                      className="h-4 w-4"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </span>
                </summary>
                <p className="mt-4 text-sm font-medium leading-relaxed text-slate-600">
                  {faq.a}
                </p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* BOTTOM CTA */}
      <section className="bg-[#2871d3] py-16 text-center text-white sm:py-20">
        <div className="mx-auto max-w-3xl px-4">
          <h2 className="mb-6 text-3xl font-black sm:text-4xl">
            Ready for your next adventure?
          </h2>
          <p className="mb-8 text-lg font-medium text-blue-100">
            Compare live rates on compact, midsize, and full-size SUVs from top
            national brands.
          </p>
          <Link
            to="/cars"
            className="inline-flex items-center gap-2 rounded-full bg-white px-8 py-4 font-black text-[#2871d3] shadow-lg transition-transform hover:-translate-y-1 hover:shadow-xl active:translate-y-0"
          >
            Search SUVs Now
            <FaArrowRight />
          </Link>
        </div>
      </section>
    </div>
  );
}
